<template>
  <v-img
    v-if="src"
    :key="reloadKey"
    :src="src"
    :alt="alt"
    :aria-busy="ariaBusy"
    v-bind="imgAttrs"
    @load="handleLoad"
    @error="handleError"
  >
    <template v-if="$slots.placeholder" #placeholder>
      <slot name="placeholder" />
    </template>
    <template v-if="$slots.error" #error>
      <slot name="error" />
    </template>
    <slot />
  </v-img>
  <slot v-else name="empty" />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch } from "vue";
import {
  registerRetryableImageController,
  type RetryableImageController,
} from "@/utils/retryableImageRegistry";

defineOptions({ inheritAttrs: false });

interface Props {
  src?: string | null;
  alt?: string;
  /**
   * When enabled, logs retry attempts to console to help debug iOS/WebView behavior.
   * Keep this off by default to avoid noisy logs.
   */
  debug?: boolean;
  /**
   * Treat "no load/error event" as stalled and retry.
   * iOS/WKWebView can drop image requests during backgrounding.
   */
  timeoutMs?: number;
  /**
   * Max total retries (not counting the initial attempt).
   */
  maxRetries?: number;
  /**
   * Minimum delay between retries to avoid thrashing.
   */
  minRetryIntervalMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
  src: null,
  alt: undefined,
  debug: false,
  timeoutMs: 15000,
  maxRetries: 2,
  minRetryIntervalMs: 1500,
});

const emit = defineEmits<{
  load: [];
  error: [event: unknown];
  retry: [payload: { attempt: number; reason: string; src: string }];
}>();

type Status = "idle" | "loading" | "loaded" | "failed";

let timer: ReturnType<typeof window.setTimeout> | undefined;
const reloadKey = ref(0);
const status = ref<Status>("idle");
const retryCount = ref(0);
const lastAttemptAt = ref<number>(0);
const finalErrorEmittedForSrc = ref<string | null>(null);

defineExpose({ status, retryCount });

const attrs = useAttrs();

const imgAttrs = computed(() => {
  // Everything except our explicit props lives in $attrs (class, aspect-ratio, cover,
  // width/height, etc.). We forward them to v-img.
  return attrs as Record<string, unknown>;
});

const ariaBusy = computed<"true" | undefined>(() => {
  // Expose loading state to assistive tech without being noisy.
  return status.value === "loading" ? "true" : undefined;
});

function debugLog(message: string, extra?: Record<string, unknown>): void {
  if (!props.debug) return;
  const payload = extra ? { ...extra } : undefined;
  if (payload) {
    console.log("[RetryableImg]", message, payload);
  } else {
    console.log("[RetryableImg]", message);
  }
}

function clearTimer(): void {
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timer = undefined;
  }
}

function isDocumentVisible(): boolean {
  if (typeof document === "undefined") return true;
  if (!("visibilityState" in document)) return true;
  return document.visibilityState === "visible";
}

function isOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  if (!("onLine" in navigator)) return true;
  return navigator.onLine;
}

function startWatchdog(): void {
  clearTimer();
  if (!props.src) return;
  if (status.value !== "loading") return;

  timer = window.setTimeout(() => {
    // If we're backgrounded/offline, wait for the registry to kick us later.
    if (!isDocumentVisible() || !isOnline()) {
      startWatchdog();
      return;
    }

    retryNow("timeout");
  }, props.timeoutMs);
}

function beginLoad(reason: string): void {
  if (!props.src) return;
  status.value = "loading";
  lastAttemptAt.value = Date.now();
  startWatchdog();

  if (reason !== "initial") {
    emit("retry", { attempt: retryCount.value, reason, src: props.src });
  }
}

function retryNow(reason: string): void {
  if (!props.src) return;
  // If we've already terminally failed for this src, don't spam retries/errors.
  if (status.value === "failed" && finalErrorEmittedForSrc.value === props.src) {
    return;
  }
  if (retryCount.value >= props.maxRetries) {
    status.value = "failed";
    clearTimer();
    // Emit a single terminal error per src after exhausting retries.
    if (finalErrorEmittedForSrc.value !== props.src) {
      finalErrorEmittedForSrc.value = props.src;
      debugLog("failed (max retries reached)", {
        src: props.src,
        retryCount: retryCount.value,
        reason,
      });
      emit("error", new Error("Image load failed after retries"));
    }
    return;
  }

  const now = Date.now();
  const since = now - lastAttemptAt.value;
  if (since < props.minRetryIntervalMs) {
    // Defer slightly to avoid tight loops.
    window.setTimeout(() => retryNow(reason), props.minRetryIntervalMs - since);
    return;
  }

  retryCount.value += 1;
  status.value = "loading";
  lastAttemptAt.value = now;
  reloadKey.value += 1; // Force remount so the browser re-issues the request.
  startWatchdog();
  debugLog("retry", {
    src: props.src,
    reason,
    attempt: retryCount.value,
  });
  emit("retry", { attempt: retryCount.value, reason, src: props.src });
}

function handleLoad(): void {
  status.value = "loaded";
  clearTimer();
  debugLog("loaded", { src: props.src, retryCount: retryCount.value });
  emit("load");
}

function handleError(event: unknown): void {
  // Some iOS failures surface as error events; retry in-place.
  if (!props.src) {
    status.value = "failed";
    clearTimer();
    debugLog("error (no src)", { event });
    emit("error", event);
    return;
  }

  if (!isDocumentVisible() || !isOnline()) {
    // If we're backgrounded/offline, don't consume a retry yet; wait for resume/online.
    status.value = "loading";
    debugLog("error while offline/hidden (waiting)", { src: props.src });
    startWatchdog();
    return;
  }

  debugLog("error (will retry)", { src: props.src, event });
  retryNow("error");
}

// Restart tracking when src changes.
watch(
  () => props.src,
  (newSrc) => {
    clearTimer();
    retryCount.value = 0;
    finalErrorEmittedForSrc.value = null;
    if (!newSrc) {
      status.value = "idle";
      return;
    }
    status.value = "loading";
    debugLog("start", { src: newSrc });
    beginLoad("initial");
  },
  { immediate: true },
);

// Global registry kick: used to re-trigger images that got "stuck" on iOS.
let unregister: (() => void) | undefined;
const id = `retryable-img-${Math.random().toString(36).slice(2)}`;

const controller: RetryableImageController = {
  getStatus: () => status.value,
  onConnectivityOrVisibilityChange: () => {
    if (!props.src) return;
    if (status.value === "loaded") return;
    if (status.value === "failed" && finalErrorEmittedForSrc.value === props.src) {
      // Terminal state for this src; don't re-trigger or re-emit errors.
      return;
    }
    // If we’re still pending/failed, give it another try (bounded).
    retryNow("resume");
  },
};

onMounted(() => {
  unregister = registerRetryableImageController(id, controller);
});

onBeforeUnmount(() => {
  unregister?.();
  clearTimer();
});
</script>
